import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import EcosystemSchema from "@/components/ecosysteme/EcosystemSchema";
import { useLang } from "@/i18n/LanguageContext";
import { SUBSIDIARIES } from "@/data/ekipData";

export default function Ecosysteme() {
  const { t, lang } = useLang();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  return (
    <div data-testid="ecosysteme-page">
      <PageHero
        overline={t.ecosysteme.hero.overline}
        title={t.ecosysteme.hero.title}
        titleItalic={t.ecosysteme.hero.titleItalic}
        subtitle={t.ecosysteme.hero.subtitle}
      />

      {/* Schema */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="ecosysteme-schema-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.ecosysteme.schema.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.ecosysteme.schema.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.ecosysteme.schema.titleItalic}</em>
            </h2>
          </div>

          <EcosystemSchema />
        </div>
      </section>

      {/* Subsidiaries detailed */}
      <section className="bg-white border-t border-ekip-gold/20" data-testid="ecosysteme-subsidiaries">
        {SUBSIDIARIES.map((s, idx) => (
          <div
            key={s.id}
            id={s.slug}
            data-testid={`subsidiary-${s.slug}`}
            className={`py-20 md:py-28 border-b border-ekip-gold/20 ${idx % 2 === 1 ? "bg-ekip-cream" : "bg-white"}`}
          >
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
              <div className="grid grid-cols-12 gap-6 md:gap-14 items-start">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7 }}
                  className={`col-span-12 md:col-span-6 ${idx % 2 === 1 ? "md:order-2" : ""}`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-[10px] uppercase tracking-[0.3em] font-medium" style={{ color: s.accent }}>
                      0{idx + 1} — {s.fullName}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-ekip-navy">
                    {s.name}
                  </h3>
                  <p className="mt-6 font-display italic text-xl md:text-2xl" style={{ color: s.accent }}>
                    « {s.tagline[lang]} »
                  </p>
                  <p className="mt-6 text-ekip-slate font-body font-light leading-relaxed max-w-xl">
                    {s.description[lang]}
                  </p>

                  <div className="mt-10 grid grid-cols-3 gap-4">
                    {s.kpis.map((k, i) => (
                      <div key={i} className="border-t pt-4" style={{ borderColor: s.accent }}>
                        <div className="font-display font-bold text-xl md:text-2xl text-ekip-navy">
                          {k.value}
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ekip-graphite">
                          {k.label[lang]}
                        </div>
                      </div>
                    ))}
                  </div>

                  <a
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`subsidiary-cta-${s.slug}`}
                    className="mt-10 inline-flex items-center gap-3 pb-2 border-b text-xs uppercase tracking-[0.28em] font-medium transition-colors"
                    style={{ color: s.accent, borderColor: `${s.accent}66` }}
                  >
                    {t.common.discover} {s.name}
                    <ArrowUpRight size={14} />
                  </a>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  className={`col-span-12 md:col-span-6 flex justify-center ${idx % 2 === 1 ? "md:order-1" : ""}`}
                >
                  <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center">
                    <div
                      className="absolute inset-0"
                      style={{ transform: "rotate(45deg)", background: s.accent }}
                    />
                    <div className="absolute inset-4 border border-white/20 pointer-events-none" style={{ transform: "rotate(45deg)" }} />
                    <div className="absolute inset-10 border border-white/10 pointer-events-none" style={{ transform: "rotate(45deg)" }} />
                    <div className="relative z-10 text-center px-8">
                      <div className="font-display font-bold text-4xl md:text-5xl text-white leading-none">
                        {s.name}
                      </div>
                      <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-white/80">
                        {s.fullName}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-ekip-navy py-20 md:py-24 text-center" data-testid="ecosysteme-cta">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <p className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl text-white">
            {t.projets.cta.title}
          </p>
          <p className="mt-4 text-white/70 font-body font-light max-w-2xl mx-auto">
            {t.projets.cta.subtitle}
          </p>
          <Link to="/contact" className="btn-primary mt-10">
            {t.projets.cta.button}
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
